import { Box } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes, GridRowId } from '@mui/x-data-grid-pro'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit, MdTopic } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useGetMaterialFactors } from 'src/api/material-factor'
import { useGetProductCategoriesForDropDown } from 'src/api/product-category'
import { useGetUnits } from 'src/api/unit'
import { StyledInfiniteScrollTable } from 'src/containers/Equipment/EquipmentListContainer'
import { useRowCount } from 'src/hooks/useViewportHeight'
import { FilterHeader } from 'src/containers/Equipment/FilterHeader'
import { productQueryKeys } from 'src/query-keys'
import { StyledCard } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import {
  invalidateProductCache,
  productsEndpoints,
  useDeleteProduct,
  useGetProduct,
  useUpdateProduct,
} from '../../../api/product'
import { CenteredSpinner } from '../../../components/Spinner'
import { IProduct, ProductFilters } from '../../../generated-types/product'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { ShowDialogProps } from '../../../show-dialog'
import { CertificationListDialog } from './product-certification/CertificationListDialog'
import ProductFilterChips from './ProductFilterChips'
import { ProductForm } from './ProductForm'

interface UpdateProductProps extends ShowDialogProps {
  id: EntityId
}

const UpdateProductDialog: React.FC<UpdateProductProps> = ({ pop, id }) => {
  const mutation = useUpdateProduct(id)
  const { t } = useTranslation(['common-api-message'])

  const onSubmit = async form => {
    await mutation.mutateAsync(form, {
      onSuccess: () => {
        toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
        invalidateProductCache.getProducts()
        pop(true)
      },
      onError: () => {
        toast.error(t('InternalError', { ns: 'common-api-message' }))
      },
    })
  }

  const { data, isLoading } = useGetProduct(id)
  if (isLoading) return <CenteredSpinner />

  return <ProductForm onSubmit={onSubmit} onCancel={() => pop()} initialValues={data as IProduct} />
}

export const ProductTable: React.FC<{
  searchValue: string
  headerFilters: ProductFilters
  setHeaderFilters: React.Dispatch<React.SetStateAction<ProductFilters>>
}> = ({ searchValue = '', headerFilters, setHeaderFilters }) => {
  const { t: translate } = useTranslation(['product', 'admin-sidebar', 'common'])
  const rowCount = useRowCount()
  const { mutateAsync, isLoading: isDeleting } = useDeleteProduct()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const { data: units } = useGetUnits()
  const { data: productCategories } = useGetProductCategoriesForDropDown()
  const { data: materialFactors } = useGetMaterialFactors()

  const onDelete = (id: EntityId) => {
    showConfirmDialog(
      translate('deleteProduct', { ns: 'product' }),
      translate('areYouSureDeleteProduct', { ns: 'product' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await mutateAsync(id, {
          onSuccess: () => {
            invalidateProductCache.getProducts()
            toast.success(translate('document-deleted'))
            pop()
          },
          onError: () => {
            toast.error(translate('error-document-deleting'))
            pop()
          },
        })
      },
    )
  }

  const onUpdate = async (id: GridRowId) => {
    const added = await showDialog(UpdateProductDialog, {
      title: translate('updateProduct', { ns: 'product' }),
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
      componentProps: { id: id },
    })
    if (!added) return

    invalidateProductCache.getProducts()
  }

  const onShowCertifications = async (id: EntityId) => {
    const added = await showDialog(
      CertificationListDialog,
      {
        componentProps: { productId: id },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
    if (!added) return
  }

  const debouncedServerFilter = useMemo(
    () =>
      debounce((filters: ProductFilters) => {
        setHeaderFilters(filters)
      }, 500),
    [setHeaderFilters],
  )

  useEffect(() => {
    return () => {
      debouncedServerFilter.cancel()
    }
  }, [debouncedServerFilter])

  const updateFilter = useCallback(
    (field: string, value: string | number | number[] | string[]) => {
      const updatedLocalFilters = { ...headerFilters }

      if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
        delete updatedLocalFilters[field]
      } else if (field === 'units') {
        const unitValues = value
          .toString()
          .split(',')
          .filter(s => s.trim())
        if (unitValues.length > 0) {
          updatedLocalFilters['units'] = unitValues
        }
      } else if (field === 'productCategories') {
        const productCategoryValues = value
          .toString()
          .split(',')
          .filter(s => s.trim())
        if (productCategoryValues.length > 0) {
          updatedLocalFilters['productCategories'] = productCategoryValues
        }
      } else if (field === 'materialFactors') {
        const materialFactorValues = value
          .toString()
          .split(',')
          .filter(s => s.trim())
        if (materialFactorValues.length > 0) {
          updatedLocalFilters['materialFactors'] = materialFactorValues
        }
      } else {
        updatedLocalFilters[field] = value
      }

      debouncedServerFilter(updatedLocalFilters)
    },
    [debouncedServerFilter, headerFilters],
  )
  const clearFilter = useCallback(
    (field: string) => {
      const updatedLocalFilters = { ...headerFilters }
      delete updatedLocalFilters[field]

      debouncedServerFilter(updatedLocalFilters)
    },
    [debouncedServerFilter, headerFilters],
  )

  const getUnitOptions = () => {
    if (units == null) return []
    return units.items.map(({ abbreviation, name }) => ({
      value: abbreviation,
      label: name + ' (' + abbreviation + ')',
    }))
  }

  const getProductCategoryOptions = () => {
    if (productCategories == null) return []
    return productCategories.map(({ id, name }) => ({
      value: id,
      label: name,
    }))
  }

  const getMaterialFactorOptions = () => {
    if (materialFactors == null) return []
    return materialFactors.items.map(({ id, description }) => ({
      value: id,
      label: description,
    }))
  }

  const getCurrentValue = (field, multiple, options) => {
    const filterValue = headerFilters[field as keyof ProductFilters]

    if (field === 'units' || field === 'productCategories' || field === 'materialFactors') {
      if (Array.isArray(filterValue)) {
        return (filterValue as string[])
          .map(value => options.find(option => String(option.value) === value))
          .filter(Boolean)
      }
      return multiple ? [] : null
    }
  }
  const handleChange = (event: React.SyntheticEvent, value: any, field: string, multiple: boolean) => {
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }
    if (field === 'units' || field === 'productCategories' || field === 'materialFactors') {
      if (multiple) {
        const arrayValue = Array.isArray(value) ? value : value ? [value] : []
        const valueSet = arrayValue.filter(v => v && typeof v === 'object' && 'value' in v).map(v => String(v.value))
        updateFilter(field, valueSet)
      } else {
        if (value && typeof value === 'object' && 'value' in value) {
          updateFilter(field, [Number(value.value)])
        } else {
          updateFilter(field, [])
        }
      }
    }
  }
  const createFilterHeader = useCallback(
    (
      headerName: string,
      field: string,
      filterType: 'text' | 'number' | 'date' | 'dropdown' = 'text',
      options?: {
        placeholder?: string
        dropdownOptions?: Array<{ value: string | number; label: string }>
        loading?: boolean
        multiple?: boolean
      },
    ) => {
      return (
        <FilterHeader
          headerName={headerName}
          field={field}
          filterType={filterType}
          placeholder={options?.placeholder}
          dropdownOptions={options?.dropdownOptions}
          loading={options?.loading}
          multiple={options?.multiple}
          filters={headerFilters}
          filterInputRefs={filterInputRefs}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          getCurrentValuesOfField={(field, multiple, options) => getCurrentValue(field, multiple, options)}
          handleChangeFields={(event: React.SyntheticEvent, value: any, field: string, multiple: boolean) =>
            handleChange(event, value, field, multiple)
          }
        />
      )
    },
    [headerFilters, filterInputRefs, updateFilter, clearFilter],
  )

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'number',
      headerName: translate('number', { ns: 'product' }),
      flex: 1,
      cellClassName: 'font-weight-semi-bold',
      renderHeader: () => createFilterHeader(translate('number', { ns: 'product' }), 'number'),
    },
    {
      field: 'description',
      headerName: translate('article-description', { ns: 'product' }),
      flex: 1,
      cellClassName: 'font-weight-semi-bold',
      renderHeader: () => createFilterHeader(translate('article-description', { ns: 'product' }), 'description'),
    },
    {
      field: 'supplier',
      headerName: translate('supplier'),
      valueGetter: params => params.row.supplier.name,
      flex: 1,
      renderHeader: () => createFilterHeader(translate('supplier', { ns: 'common' }), 'supplierName'),
    },
    {
      field: 'unit',
      headerName: translate('unit'),
      valueGetter: params => params.row.unit.name + ' (' + params.row.unit.abbreviation + ')',
      flex: 1,
      renderHeader: () =>
        createFilterHeader(translate('unit', { ns: 'product' }), 'units', 'dropdown', {
          dropdownOptions: getUnitOptions(),
          multiple: true,
        }),
    },
    {
      field: 'expectedLifetime',
      headerName: translate('expectedLifetime'),
      flex: 1,
      renderHeader: () => createFilterHeader(translate('expectedLifetime'), 'expectedLifetime'),
    },
    {
      field: 'minimumBreakingLoad',
      headerName: translate('minimumBreakingLoad'),
      flex: 1,
      renderHeader: () => createFilterHeader(translate('minimumBreakingLoad'), 'minimumBreakingLoad'),
    },

    {
      field: 'productCategory',
      headerName: translate('category', { ns: 'common' }),
      valueGetter: params => params.row.productCategory?.name,
      flex: 1,
      renderHeader: () =>
        createFilterHeader(translate('category', { ns: 'common' }), 'productCategories', 'dropdown', {
          dropdownOptions: getProductCategoryOptions(),
          multiple: true,
        }),
    },
    {
      field: 'materialFactor',
      headerName: translate('material-factor', { ns: 'admin-sidebar' }),
      valueGetter: params => params.row.materialFactor?.description,
      flex: 1,
      renderHeader: () =>
        createFilterHeader(translate('material-factor', { ns: 'admin-sidebar' }), 'materialFactors', 'dropdown', {
          dropdownOptions: getMaterialFactorOptions(),
          multiple: true,
        }),
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ id }) => [
          <GridActionsCellItem
            key={id}
            label={translate('certificationList', { ns: 'product-certification' })}
            icon={<MdTopic size={24} />}
            onClick={() => onShowCertifications(id)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(id)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => onDelete(id)}
            disabled={isDeleting}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [isDeleting, searchValue, translate, createFilterHeader, units, productCategories, materialFactors],
  )

  return (
    <>
      <StyledCard className="p-2 mb-2" height={window.innerHeight - 252 + 'px'}>
        <Box padding={1}>
          <StyledInfiniteScrollTable<IProduct>
            id="product-table"
            queryUrl={productsEndpoints.getProducts()}
            pageSize={rowCount}
            queryParams={{ searchInput: searchValue, ...headerFilters }}
            columns={columns}
            generateQueryKey={(page, pageSize) =>
              productQueryKeys.GET_PRODUCTS(searchValue, { ...headerFilters, page, pageSize })
            }
            toolbarFilter={
              <ProductFilterChips
                headerFilters={headerFilters}
                clearFilter={clearFilter}
                updateFilter={updateFilter}
                units={units?.items}
                materialFactors={materialFactors?.items}
                categories={productCategories}
              />
            }
            enableToolbar={true}
            enableToolbarFilter={true}
            height={window.innerHeight - 290 + 'px'}
          />
        </Box>
      </StyledCard>
    </>
  )
}
